import { useRef, useState } from "react"

import { Guid } from "@/types"
import Asset from "models/Asset"
import AssetService from "services/AssetService"
import i18n from "intl/i18n"
import { Currency } from "config/constants"
import { LmidModalMode } from "components/LmidModal/lmidModalMode"
import buildCopyAssetPayload from "./buildCopyAssetPayload"
import { ICopyAssetRow, ICopyAssetState, effectiveValue } from "./copyAssetRows"

export enum CopyAssetCreationStatus {
    pending = "pending",
    creating = "creating",
    success = "success",
    failed = "failed",
}

export interface ICopyAssetCreationResult {
    status: CopyAssetCreationStatus
    assetId?: Guid
    lmid?: string
    errorMessage?: string
}

export interface ICopyAssetCreationState {
    /** Whether a creation run has been started; the dialog then shows the results view. */
    hasStarted: boolean
    /** Whether a creation call is in flight; nothing else may start while it is. */
    isRunning: boolean
    resultsByRowId: Record<string, ICopyAssetCreationResult>
}

/**
 * Creates the copies, strictly one after the other.
 *
 * Serialization is deliberate and load-bearing: copies saved without an LMID
 * derive their placeholder index from the assets already stored, so two
 * concurrent creates with the same internal order number could collide. An
 * in-flight guard rejects any attempt to start a run — or a retry — while a
 * call is still running, and each row's create is awaited before the next
 * starts.
 */
export default function useCopyAssetCreation({
    source,
    isExtManufacturer,
    defaultCurrency,
}: {
    source: Asset
    isExtManufacturer: boolean
    defaultCurrency: Currency
}) {
    const [creationState, setCreationState] = useState<ICopyAssetCreationState>({
        hasStarted: false,
        isRunning: false,
        resultsByRowId: {},
    })
    const inFlightRef = useRef(false)
    const createdLmidsRef = useRef<Set<string>>(new Set())

    function setRowResult(rowId: string, result: ICopyAssetCreationResult) {
        setCreationState(current => ({
            ...current,
            resultsByRowId: { ...current.resultsByRowId, [rowId]: result },
        }))
    }

    async function createOne(state: ICopyAssetState, row: ICopyAssetRow): Promise<void> {
        setRowResult(row.id, { status: CopyAssetCreationStatus.creating })
        try {
            let lmid: string | undefined
            let createAssetLink = row.createAssetLink

            if (row.lmidMode === LmidModalMode.enterLmid) {
                lmid = row.lmid.trim()
                if (createdLmidsRef.current.has(lmid)) {
                    setRowResult(row.id, {
                        status: CopyAssetCreationStatus.failed,
                        errorMessage: i18n.t('copyAsset.lmidJustCreated'),
                    })
                    return
                }
            } else if (row.lmidMode === LmidModalMode.requestLmid) {
                const response = await AssetService.requestNewLmid({ isExtManufacturer })
                if (!response) {
                    setRowResult(row.id, { status: CopyAssetCreationStatus.failed })
                    return
                }
                lmid = response.lmid
                createAssetLink = response.createAssetLink
            } else {
                const response = await AssetService.getTempLmid({
                    isExtManufacturer,
                    internalOrderNumber: effectiveValue(state, row, "internalOrderNumber").trim(),
                })
                lmid = undefined
                createAssetLink = response.createAssetLink
            }

            if (!createAssetLink) {
                setRowResult(row.id, { status: CopyAssetCreationStatus.failed })
                return
            }

            const payload = buildCopyAssetPayload({ source, state, row, lmid, defaultCurrency })
            const created = await AssetService.createAsset(createAssetLink, payload)

            createdLmidsRef.current.add(created.lmid)
            setRowResult(row.id, {
                status: CopyAssetCreationStatus.success,
                assetId: created.id,
                lmid: created.lmid,
            })
        } catch (e) {
            setRowResult(row.id, {
                status: CopyAssetCreationStatus.failed,
                errorMessage: e instanceof Error && e.message ? e.message : undefined,
            })
        }
    }

    async function run(state: ICopyAssetState): Promise<void> {
        if (inFlightRef.current) return
        inFlightRef.current = true

        setCreationState({
            hasStarted: true,
            isRunning: true,
            resultsByRowId: Object.fromEntries(
                state.rows.map(row => [row.id, { status: CopyAssetCreationStatus.pending }]),
            ),
        })

        try {
            for (const row of state.rows) {
                await createOne(state, row)
            }
        } finally {
            inFlightRef.current = false
            setCreationState(current => ({ ...current, isRunning: false }))
        }
    }

    async function retry(state: ICopyAssetState, rowId: string): Promise<void> {
        if (inFlightRef.current) return
        const row = state.rows.find(candidate => candidate.id === rowId)
        if (!row) return
        inFlightRef.current = true
        setCreationState(current => ({ ...current, isRunning: true }))
        try {
            await createOne(state, row)
        } finally {
            inFlightRef.current = false
            setCreationState(current => ({ ...current, isRunning: false }))
        }
    }

    return {
        creationState,
        run,
        retry,
    }
}
